import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, ShieldOff, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { API, errorText } from "@/lib/payments";
import { RecoveryCodes } from "@/components/admin/RecoveryCodes";
import { TeamCard } from "@/components/admin/TeamCard";

const MyAccountCard = ({ admin, onRefresh }) => {
  const [pw, setPw] = useState({ current_password: "", new_password: "", code: "" });
  const [regenCode, setRegenCode] = useState("");
  const [codes, setCodes] = useState(null);

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/password`, pw);
      toast.success("Password changed. Other sessions were signed out.");
      setPw({ current_password: "", new_password: "", code: "" });
    } catch (err) {
      toast.error(errorText(err, "Could not change password"));
    }
  };
  const regenerate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/auth/recovery-codes`, { code: regenCode });
      setCodes(data.recovery_codes);
      setRegenCode("");
      onRefresh();
    } catch (err) {
      toast.error(errorText(err, "Could not create new codes"));
    }
  };
  const set = (k) => (e) => setPw((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Card data-testid="my-account-card">
      <CardHeader><CardTitle className="font-heading text-lg">My account</CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-8">
        <form onSubmit={changePassword} className="space-y-3">
          <p className="text-sm font-medium">Change password</p>
          <div><Label>Current password</Label><Input type="password" required value={pw.current_password} onChange={set("current_password")} data-testid="change-pw-current-input" /></div>
          <div><Label>New password (12+ characters)</Label><Input type="password" required value={pw.new_password} onChange={set("new_password")} data-testid="change-pw-new-input" /></div>
          <div><Label>Authenticator code</Label><Input inputMode="numeric" required value={pw.code} onChange={set("code")} data-testid="change-pw-code-input" /></div>
          <Button type="submit" size="sm" data-testid="change-pw-submit-btn">Change password</Button>
        </form>
        <form onSubmit={regenerate} className="space-y-3">
          <p className="text-sm font-medium">Recovery codes</p>
          <p className="text-sm text-stone-600" data-testid="recovery-codes-left">{admin.recovery_codes_left} of 10 unused codes left. Creating new codes cancels the old ones.</p>
          <div><Label>Authenticator code</Label><Input inputMode="numeric" required value={regenCode} onChange={(e) => setRegenCode(e.target.value)} data-testid="regen-codes-code-input" /></div>
          <Button type="submit" size="sm" variant="outline" data-testid="regen-codes-btn"><KeyRound className="h-4 w-4 mr-1" />Create new recovery codes</Button>
        </form>
      </CardContent>
      <Dialog open={!!codes} onOpenChange={(o) => !o && setCodes(null)}>
        <DialogContent data-testid="new-recovery-codes-dialog">
          <DialogHeader><DialogTitle>New recovery codes</DialogTitle></DialogHeader>
          {codes && <RecoveryCodes codes={codes} email={admin.email} onDone={() => setCodes(null)} doneLabel="Done" />}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const LegacyCard = ({ team, onDisabled }) => {
  const [open, setOpen] = useState(false);
  if (!team) return null;
  const disable = async () => {
    try {
      await axios.post(`${API}/auth/legacy/disable`);
      toast.success("Old shared login disabled");
      onDisabled();
    } catch (err) {
      toast.error(errorText(err, "Could not disable"));
    }
  };
  return (
    <Card data-testid="legacy-login-card">
      <CardHeader><CardTitle className="font-heading text-lg">Old shared login</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        {team.legacy_login_enabled ? (
          <>
            <p className="text-amber-800" data-testid="legacy-status-enabled">The shared login <b>{team.legacy_email}</b> still works and has no two-step verification. Disable it once you've confirmed your own sign-in works and your recovery codes are saved.</p>
            <Button variant="destructive" size="sm" onClick={() => setOpen(true)} data-testid="legacy-disable-btn"><ShieldOff className="h-4 w-4 mr-1" />Disable old shared login</Button>
          </>
        ) : <p className="text-green-700" data-testid="legacy-status-disabled">Disabled. Only personal accounts with two-step verification can sign in.</p>}
      </CardContent>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable the old shared login?</AlertDialogTitle>
            <AlertDialogDescription>Anyone using {team.legacy_email} will be signed out and can no longer sign in. This cannot be undone from the dashboard.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Back</AlertDialogCancel>
            <AlertDialogAction onClick={disable} data-testid="legacy-disable-confirm-btn">Disable</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

const WebhookCredsCard = () => {
  const [creds, setCreds] = useState(null);
  const reveal = async () => {
    try {
      setCreds((await axios.get(`${API}/admin/phonepe-webhook-credentials`)).data);
    } catch (err) {
      toast.error(errorText(err, "Could not load"));
    }
  };
  return (
    <Card data-testid="webhook-creds-card">
      <CardHeader><CardTitle className="font-heading text-lg">PhonePe webhook settings</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-2">
        {!creds ? <Button size="sm" variant="outline" onClick={reveal} data-testid="webhook-creds-reveal-btn"><Eye className="h-4 w-4 mr-1" />Show webhook username &amp; password</Button> : (
          <div className="font-mono text-xs space-y-1 bg-stone-50 rounded-lg p-3" data-testid="webhook-creds-values">
            <p>URL: {window.location.origin}{creds.webhook_path}</p><p>Auth: SHA (username &amp; password)</p>
            <p>Username: {creds.username}</p><p>Password: {creds.password}</p><p>Merchant ID: {creds.merchant_id}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const SecurityTab = ({ admin, onRefresh }) => {
  const [team, setTeam] = useState(null);
  const onTeam = useCallback((t) => setTeam(t), []);
  if (admin.role === "legacy") {
    return <p className="text-sm text-stone-600" data-testid="security-legacy-notice">Security settings are only available to personal accounts. Sign in with your own account to manage the team.</p>;
  }
  return (
    <div className="space-y-6" data-testid="security-tab">
      <MyAccountCard admin={admin} onRefresh={onRefresh} />
      {admin.role === "owner" && (
        <>
          <TeamCard onLegacyLoaded={onTeam} />
          <LegacyCard team={team} onDisabled={() => setTeam((t) => ({ ...t, legacy_login_enabled: false }))} />
          <WebhookCredsCard />
        </>
      )}
    </div>
  );
};
